/**
 * Shop Hours Utilities for OptiPlace Auto-Advance Logic
 * 
 * These utilities handle shop-timezone-aware date/time checks
 * and delegate business day logic to businessDayCalculator.ts
 */

import { toZonedTime } from 'date-fns-tz';
import { format, addDays, isSameDay, isBefore, startOfDay } from 'date-fns';
import { isBusinessDay, Holiday } from './businessDayCalculator';
// DTC-G G2: route the scheduling-validity "now" anchor in
// `isAtOrPastClosing` and `isDateInPast` through the canonical clock so
// closing-time + past-date business decisions are deterministic under
// `clock.setFixed(...)`. The `toZonedTime(now, timezone)` derivation
// (class-C already-zoned) is preserved.
import { clock } from './clock';

export interface ShopHours {
  [dayOfWeek: string]: { open: string; close: string } | null;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Get shop hours for a specific date
 * Handles both lowercase ("monday") and capitalized ("Monday") day name formats
 */
export function getShopHoursForDay(
  date: Date,
  shopHours: ShopHours,
  timezone: string
): { open: string; close: string } | null {
  const zonedDate = toZonedTime(date, timezone);
  const dayIndex = zonedDate.getDay();
  const lowercaseName = DAY_NAMES[dayIndex]; // e.g., "monday"
  const capitalizedName = lowercaseName.charAt(0).toUpperCase() + lowercaseName.slice(1); // e.g., "Monday"
  
  // Try both formats for compatibility with different database formats
  return shopHours[lowercaseName] || shopHours[capitalizedName] || null;
}

/**
 * Check if a date has configured shop hours
 */
export function hasShopHoursForDay(
  date: Date,
  shopHours: ShopHours,
  timezone: string
): boolean {
  const hours = getShopHoursForDay(date, shopHours, timezone);
  return hours !== null && !!hours.open && !!hours.close;
}

/**
 * Check if current time is at or past closing for the given date
 * Only returns true if the date is "today" in shop timezone AND now >= closing
 */
export function isAtOrPastClosing(
  checkDate: Date,
  shopHours: ShopHours,
  timezone: string
): boolean {
  const now = clock.now();
  const zonedNow = toZonedTime(now, timezone);
  const zonedCheckDate = toZonedTime(checkDate, timezone);
  
  // Only applies if checkDate is today
  if (!isSameDay(zonedNow, zonedCheckDate)) {
    return false;
  }
  
  const hours = getShopHoursForDay(checkDate, shopHours, timezone);
  if (!hours || !hours.close) {
    // No hours configured = treat as closed
    return true;
  }
  
  // Parse closing time (HH:mm format)
  const [closeHour, closeMinute] = hours.close.split(':').map(Number);
  const currentHour = zonedNow.getHours();
  const currentMinute = zonedNow.getMinutes();
  
  // Check if current time >= closing time
  if (currentHour > closeHour) return true;
  if (currentHour === closeHour && currentMinute >= closeMinute) return true;
  
  return false;
}

/**
 * Check if a date is in the past (before today in shop timezone)
 */
export function isDateInPast(
  checkDate: Date,
  timezone: string
): boolean {
  const now = clock.now();
  const zonedNow = toZonedTime(now, timezone);
  const zonedCheckDate = toZonedTime(checkDate, timezone);
  
  const todayStart = startOfDay(zonedNow);
  const checkDateStart = startOfDay(zonedCheckDate);
  
  return isBefore(checkDateStart, todayStart);
}

/**
 * Check if a date is valid for scheduling:
 * - Not in the past
 * - Not past closing (if today)
 * - Is a business day (not weekend/holiday)
 * - Has shop hours configured
 */
export function isValidSchedulingDate(
  date: Date,
  shopHours: ShopHours,
  holidays: Holiday[],
  timezone: string
): boolean {
  // Check if date is in the past
  if (isDateInPast(date, timezone)) {
    return false;
  }
  
  // Check if today and past closing
  if (isAtOrPastClosing(date, shopHours, timezone)) {
    return false;
  }
  
  // Check if it's a business day (delegates to businessDayCalculator.ts)
  if (!isBusinessDay(date, holidays, timezone)) {
    return false;
  }
  
  // Check if shop has hours configured for this day
  if (!hasShopHoursForDay(date, shopHours, timezone)) {
    return false;
  }
  
  return true;
}

/**
 * Find the next open business day starting from a given date.
 * Returns null if no valid day found within maxDaysLookahead.
 * 
 * Delegates weekend/holiday logic to businessDayCalculator.ts
 */
export function getNextOpenDay(
  startDate: Date,
  shopHours: ShopHours,
  holidays: Holiday[],
  timezone: string,
  maxDaysLookahead: number = 14
): Date | null {
  let candidate = startDate;
  
  for (let i = 0; i <= maxDaysLookahead; i++) {
    if (i > 0) {
      candidate = addDays(startDate, i);
    }
    
    if (isValidSchedulingDate(candidate, shopHours, holidays, timezone)) {
      return candidate;
    }
  }
  
  return null;
}
